/*
  <bloom-header> — sticky header + premium multi-panel mobile drawer.
  - Drawer: open/close via burger, scrim, close button, Esc, swipe.
    Locks body scroll, traps focus, restores focus to the trigger on close.
  - Panels: tapping a parent item slides to a "second screen" sub-panel with a
    Back button (iOS-style push nav). Swipe-right on a sub-panel goes back.
  - Sticky: adds .is-scrolled once the page scrolls (for shadow).
  All navigation is server-rendered, so it works with JS off (links stay reachable
  in the drawer markup and via the full menu at larger sizes).
*/
class BloomHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('.bloom-header__drawer');
    this.opener = this.querySelector('[data-drawer-open]');
    this.closers = Array.from(this.querySelectorAll('[data-drawer-close]'));
    this.panels = this.querySelector('[data-panels]');
    this.rootPanel = this.querySelector('.bloom-header__panel--root');
    this.activePanel = null;
    this.panelOpener = null;

    if (this.opener && this.drawer) {
      this.opener.addEventListener('click', () => this.open());
      this.closers.forEach((c) => c.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          if (this.activePanel) this.closePanel();
          else this.close();
        }
      });
      this.initFocusTrap();
      this.initSwipe();
    }

    // Second-screen panels
    this.querySelectorAll('[data-panel-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openPanel(btn));
    });
    this.querySelectorAll('[data-panel-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closePanel());
    });

    // Sticky scrolled state
    if (this.classList.contains('bloom-header--sticky')) {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    document.body.style.overflow = '';
  }

  open() {
    this.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.lastFocus = document.activeElement;
    const first = this.drawer.querySelector('[data-drawer-close], a, button');
    window.requestAnimationFrame(() => first && first.focus());
  }

  close() {
    this.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    if (this.opener) this.opener.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    // reset to root panel for next open
    window.setTimeout(() => this.resetPanels(), 300);
  }

  openPanel(btn) {
    const id = btn.getAttribute('data-panel-open');
    const panel = this.panels && this.panels.querySelector('[data-panel="' + id + '"]');
    if (!panel) return;
    if (this.activePanel) this.activePanel.classList.remove('is-active');
    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    if (this.panels) this.panels.classList.add('has-sub');
    if (this.rootPanel) this.rootPanel.setAttribute('aria-hidden', 'true');
    this.activePanel = panel;
    this.panelOpener = btn;
    const back = panel.querySelector('[data-panel-close]');
    window.requestAnimationFrame(() => back && back.focus());
  }

  closePanel() {
    if (!this.activePanel) return;
    this.activePanel.classList.remove('is-active');
    this.activePanel.setAttribute('aria-hidden', 'true');
    if (this.panels) this.panels.classList.remove('has-sub');
    if (this.rootPanel) this.rootPanel.setAttribute('aria-hidden', 'false');
    this.activePanel = null;
    const opener = this.panelOpener;
    this.panelOpener = null;
    window.requestAnimationFrame(() => opener && opener.focus());
  }

  resetPanels() {
    if (this.activePanel) {
      this.activePanel.classList.remove('is-active');
      this.activePanel.setAttribute('aria-hidden', 'true');
    }
    if (this.panels) this.panels.classList.remove('has-sub');
    if (this.rootPanel) this.rootPanel.setAttribute('aria-hidden', 'false');
    this.activePanel = null;
    this.panelOpener = null;
  }

  initFocusTrap() {
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusable).filter((el) => el.offsetParent !== null);
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  initSwipe() {
    let startX = 0;
    let tracking = false;
    this.drawer.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; tracking = true; }, { passive: true });
    this.drawer.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - startX;
      if (dx > 50 && this.activePanel) this.closePanel();
      else if (dx < -50 && !this.activePanel) this.close();
    }, { passive: true });
  }
}

if (!customElements.get('bloom-header')) {
  customElements.define('bloom-header', BloomHeader);
}
